import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Zap, Play, Square, Check, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PhoneNumberCard } from "@/components/phone-number-card";

const PREVIEW_TEXT = "Hello, I'm Zeus. I can help you manage your goals, tasks, and habits. This is how I'll sound.";

const PREMIUM_VOICES: Array<{ id: string; label: string; gender: "Female" | "Male" | null; description: string }> = [
  { id: "alloy", label: "Alloy", gender: null, description: "Balanced and versatile" },
  { id: "echo", label: "Echo", gender: "Male", description: "Warm and steady" },
  { id: "fable", label: "Fable", gender: "Male", description: "Expressive, British-leaning" },
  { id: "onyx", label: "Onyx", gender: "Male", description: "Deep and authoritative" },
  { id: "nova", label: "Nova", gender: "Female", description: "Bright and friendly" },
  { id: "shimmer", label: "Shimmer", gender: "Female", description: "Soft and clear" },
];

const premiumUri = (id: string) => `premium:${id}`;

const FEMALE_HINTS = [
  "female", "woman", "samantha", "victoria", "karen", "moira", "tessa", "fiona",
  "zira", "susan", "hazel", "kate", "serena", "allison", "ava", "joana", "monica",
  "paulina", "amelie", "anna", "carmit", "damayanti", "ellen", "ioana", "kanya",
  "kyoko", "laura", "lekha", "luciana", "mariska", "meijia", "melina", "milena",
  "nora", "sara", "satu", "sinji", "tingting", "veena", "yelda", "yuna", "zosia",
  "zuzana", "catherine", "emma", "jenny", "aria", "michelle", "sonia", "natasha",
  "hayley", "clara", "libby", "olivia", "amber", "ana", "ashley", "cora", "elizabeth",
  "eloise", "jane", "nancy", "sarah",
];
const MALE_HINTS = [
  "male", "man", "daniel", "alex", "fred", "david", "mark", "george", "rishi",
  "aaron", "arthur", "albert", "bruce", "diego", "jorge", "juan", "luca", "maged",
  "thomas", "xander", "yuri", "guy", "ryan", "william", "james", "ethan", "andrew",
  "brian", "christopher", "eric", "jacob", "liam", "oliver", "roger", "steffan",
  "tom", "gordon", "russell", "neil",
];

function inferGender(name: string): "Female" | "Male" | null {
  const n = name.toLowerCase();
  if (FEMALE_HINTS.some((h) => n.includes(h))) return "Female";
  if (MALE_HINTS.some((h) => n.includes(h))) return "Male";
  return null;
}

const ACCENT_LABELS: Record<string, string> = {
  "en-US": "American",
  "en-GB": "British",
  "en-AU": "Australian",
  "en-IE": "Irish",
  "en-IN": "Indian",
  "en-ZA": "South African",
  "en-CA": "Canadian",
  "en-NZ": "New Zealand",
  "en-GB-SCT": "Scottish",
  "en-NG": "Nigerian",
  "en-PH": "Filipino",
  "en-HK": "Hong Kong",
  "en-KE": "Kenyan",
  "en-SG": "Singaporean",
  "en-TZ": "Tanzanian",
};

function accentLabel(lang: string): string {
  const normalized = lang.replace("_", "-");
  if (ACCENT_LABELS[normalized]) return `${ACCENT_LABELS[normalized]} English`;
  if (normalized.toLowerCase().startsWith("en")) return `English (${normalized})`;
  try {
    const dn = new Intl.DisplayNames([navigator.language || "en"], { type: "language" });
    return dn.of(normalized) || normalized;
  } catch {
    return normalized;
  }
}

export default function ZeusSettingsPage() {
  const { toast } = useToast();
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [previewingUri, setPreviewingUri] = useState<string | null>(null);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [showAllLanguages, setShowAllLanguages] = useState(false);

  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (!ttsSupported) return;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", load);
      window.speechSynthesis.cancel();
      premiumAudioRef.current?.pause();
      premiumAudioRef.current = null;
    };
  }, [ttsSupported]);

  const { data: prefs } = useQuery<{ zeusVoiceUri?: string | null; zeusVoiceName?: string | null; zeusVoiceRate?: number | null }>({
    queryKey: ["/api/user/preferences"],
  });

  const savedRate = prefs?.zeusVoiceRate ?? 1;
  const getRate = (voiceUri: string) =>
    rates[voiceUri] ?? (prefs?.zeusVoiceUri === voiceUri ? savedRate : 1);

  const saveMutation = useMutation({
    mutationFn: async (payload: { zeusVoiceUri: string | null; zeusVoiceName: string | null; zeusVoiceRate: number | null }) => {
      return apiRequest("PATCH", "/api/user/preferences", payload);
    },
    onSuccess: (_res, payload) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
      toast({
        title: payload.zeusVoiceUri ? "Zeus voice updated" : "Zeus voice reset",
        description: payload.zeusVoiceName
          ? `Zeus will now speak with the "${payload.zeusVoiceName}" voice at ${(payload.zeusVoiceRate ?? 1).toFixed(1)}× speed.`
          : "Zeus will use the default voice.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Could not save voice", description: err.message, variant: "destructive" });
    },
  });

  const sortedVoices = useMemo(() => {
    const filtered = showAllLanguages
      ? voices
      : voices.filter((v) => v.lang.toLowerCase().startsWith("en"));
    return [...filtered].sort((a, b) => a.lang.localeCompare(b.lang) || a.name.localeCompare(b.name));
  }, [voices, showAllLanguages]);

  const premiumAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopPremiumPreview = () => {
    premiumAudioRef.current?.pause();
    premiumAudioRef.current = null;
  };

  const previewPremiumVoice = async (voiceId: string) => {
    const uri = premiumUri(voiceId);
    if (previewingUri === uri) {
      stopPremiumPreview();
      setPreviewingUri(null);
      return;
    }
    stopPremiumPreview();
    if (ttsSupported) window.speechSynthesis.cancel();
    setPreviewingUri(uri);
    try {
      const res = await apiRequest("POST", "/api/zeus/tts", { text: PREVIEW_TEXT, voice: voiceId });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audio.playbackRate = getRate(uri);
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        setPreviewingUri((cur) => (cur === uri ? null : cur));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setPreviewingUri((cur) => (cur === uri ? null : cur));
      };
      premiumAudioRef.current = audio;
      await audio.play();
    } catch (err) {
      setPreviewingUri((cur) => (cur === uri ? null : cur));
      toast({
        title: "Preview failed",
        description: err instanceof Error ? err.message : "Could not play the premium voice preview.",
        variant: "destructive",
      });
    }
  };

  const previewVoice = (voice: SpeechSynthesisVoice) => {
    if (!ttsSupported) return;
    if (previewingUri === voice.voiceURI) {
      window.speechSynthesis.cancel();
      setPreviewingUri(null);
      return;
    }
    stopPremiumPreview();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(PREVIEW_TEXT);
    u.voice = voice;
    u.rate = getRate(voice.voiceURI);
    u.onend = () => setPreviewingUri((cur) => (cur === voice.voiceURI ? null : cur));
    u.onerror = () => setPreviewingUri((cur) => (cur === voice.voiceURI ? null : cur));
    setPreviewingUri(voice.voiceURI);
    window.speechSynthesis.speak(u);
  };

  const selectedUri = prefs?.zeusVoiceUri ?? null;

  return (
    <div className="w-full space-y-6 p-4 md:p-6" data-testid="page-zeus-settings">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 shadow-md shadow-yellow-500/20">
          <Zap className="h-5 w-5 text-white" style={{ fill: "white" }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Zeus Settings</h1>
          <p className="text-sm text-muted-foreground">Customize how Zeus talks to you.</p>
        </div>
      </div>

      <PhoneNumberCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" /> Premium AI Voices
          </CardTitle>
          <CardDescription>
            Studio-quality AI voices that sound the same on every device. Premium voice audio is
            generated by our AI service and billed to your subscription like other AI features.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border/60 rounded-lg border border-border/60" data-testid="list-premium-voices">
            {PREMIUM_VOICES.map((pv) => {
              const uri = premiumUri(pv.id);
              const isSelected = selectedUri === uri;
              const isPreviewing = previewingUri === uri;
              const rate = getRate(uri);
              return (
                <div
                  key={pv.id}
                  className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 ${isSelected ? "bg-amber-500/10" : ""}`}
                  data-testid={`row-premium-voice-${pv.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{pv.label}</span>
                      <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-[10px] text-white">Premium</Badge>
                      {isSelected && (
                        <Badge className="gap-1 bg-gradient-to-r from-yellow-500 to-amber-600 text-white">
                          <Check className="h-3 w-3" /> Current voice
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary" className="text-[10px]">{pv.description}</Badge>
                      {pv.gender && <Badge variant="outline" className="text-[10px]">{pv.gender}</Badge>}
                    </div>
                  </div>
                  <div className="flex w-full shrink-0 flex-wrap items-center gap-3 sm:w-auto">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Speed</span>
                      <Slider
                        value={[rate]}
                        min={0.5}
                        max={2}
                        step={0.1}
                        className="w-28"
                        onValueChange={([v]) => setRates((r) => ({ ...r, [uri]: v }))}
                        data-testid={`slider-premium-voice-speed-${pv.id}`}
                      />
                      <span className="w-9 text-xs font-medium tabular-nums">{rate.toFixed(1)}×</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => previewPremiumVoice(pv.id)}
                      data-testid={`button-preview-premium-voice-${pv.id}`}
                    >
                      {isPreviewing ? (
                        <>
                          <Square className="h-3.5 w-3.5" /> Stop
                        </>
                      ) : (
                        <>
                          <Play className="h-3.5 w-3.5" /> Listen
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      disabled={saveMutation.isPending || (isSelected && rate === savedRate)}
                      className={
                        isSelected && rate === savedRate
                          ? ""
                          : "bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:from-yellow-600 hover:to-amber-700"
                      }
                      onClick={() =>
                        saveMutation.mutate({
                          zeusVoiceUri: uri,
                          zeusVoiceName: `Premium ${pv.label}`,
                          zeusVoiceRate: rate,
                        })
                      }
                      data-testid={`button-select-premium-voice-${pv.id}`}
                    >
                      {isSelected ? (rate === savedRate ? "Selected" : "Save speed") : "Use this voice"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" /> Zeus Voice
          </CardTitle>
          <CardDescription>
            Choose the voice Zeus uses when reading answers aloud. Preview any voice, then select the
            one you like — you can change it anytime. Available voices come from your device and
            browser, so the list may differ between devices.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!ttsSupported ? (
            <p className="text-sm text-muted-foreground">
              Your browser does not support speech synthesis, so Zeus voice replies are unavailable here.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant={showAllLanguages ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => setShowAllLanguages((s) => !s)}
                    data-testid="button-toggle-all-languages"
                  >
                    {showAllLanguages ? "Showing all languages" : "Show all languages"}
                  </Button>
                  {selectedUri && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => saveMutation.mutate({ zeusVoiceUri: null, zeusVoiceName: null, zeusVoiceRate: null })}
                      disabled={saveMutation.isPending}
                      data-testid="button-reset-zeus-voice"
                    >
                      Reset to default voice
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {sortedVoices.length} voice{sortedVoices.length === 1 ? "" : "s"} available
                </p>
              </div>

              {sortedVoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">Loading voices from your device…</p>
              ) : (
                <div className="divide-y divide-border/60 rounded-lg border border-border/60">
                  {sortedVoices.map((voice) => {
                    const gender = inferGender(voice.name);
                    const isSelected = selectedUri === voice.voiceURI;
                    const isPreviewing = previewingUri === voice.voiceURI;
                    const rate = getRate(voice.voiceURI);
                    return (
                      <div
                        key={voice.voiceURI}
                        className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 ${
                          isSelected ? "bg-amber-500/10" : ""
                        }`}
                        data-testid={`row-zeus-voice-${voice.name.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-medium">{voice.name}</span>
                            {isSelected && (
                              <Badge className="gap-1 bg-gradient-to-r from-yellow-500 to-amber-600 text-white">
                                <Check className="h-3 w-3" /> Current voice
                              </Badge>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant="secondary" className="text-[10px]">{accentLabel(voice.lang)}</Badge>
                            {gender && <Badge variant="outline" className="text-[10px]">{gender}</Badge>}
                            {voice.localService ? (
                              <Badge variant="outline" className="text-[10px]">On-device</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">Online</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex w-full shrink-0 flex-wrap items-center gap-3 sm:w-auto">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Speed</span>
                            <Slider
                              value={[rate]}
                              min={0.5}
                              max={2}
                              step={0.1}
                              className="w-28"
                              onValueChange={([v]) => setRates((r) => ({ ...r, [voice.voiceURI]: v }))}
                              data-testid={`slider-voice-speed-${voice.name.replace(/\s+/g, "-").toLowerCase()}`}
                            />
                            <span className="w-9 text-xs font-medium tabular-nums">{rate.toFixed(1)}×</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => previewVoice(voice)}
                            data-testid={`button-preview-voice-${voice.name.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {isPreviewing ? (
                              <>
                                <Square className="h-3.5 w-3.5" /> Stop
                              </>
                            ) : (
                              <>
                                <Play className="h-3.5 w-3.5" /> Listen
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            disabled={saveMutation.isPending || (isSelected && rate === savedRate)}
                            className={
                              isSelected && rate === savedRate
                                ? ""
                                : "bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:from-yellow-600 hover:to-amber-700"
                            }
                            onClick={() =>
                              saveMutation.mutate({
                                zeusVoiceUri: voice.voiceURI,
                                zeusVoiceName: voice.name,
                                zeusVoiceRate: rate,
                              })
                            }
                            data-testid={`button-select-voice-${voice.name.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {isSelected ? (rate === savedRate ? "Selected" : "Save speed") : "Use this voice"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
